import React, { useState, useEffect, useRef } from 'react';
import { searchGifs, fetchTrendingGifs, getGifUrl, getGifPreviewUrl } from '../utils/tenorApi';
import AiGifPanel from './AiGifPanel';
import './TenorGifPicker.css';

function TenorGifPicker({ onSelectGif, onClose }) {
  const [mode, setMode] = useState('search'); // 'search' | 'ai'
  const [gifs, setGifs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const searchTimeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Load trending GIFs on mount
  useEffect(() => {
    loadTrendingGifs();
  }, []);

  const loadTrendingGifs = async () => {
    setLoading(true);
    const trendingGifs = await fetchTrendingGifs(12);
    setGifs(trendingGifs);
    setLoading(false);
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search
    searchTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      const results = await searchGifs(query, 12);
      setGifs(results);
      setLoading(false);
    }, 500);
  };

  const handleGifSelect = (gif) => {
    const gifUrl = getGifUrl(gif);
    const gifTitle = gif.content_description || 'GIF';
    onSelectGif(gifUrl, gifTitle);
    onClose();
  };

  // Close on clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div className="tenor-gif-picker-overlay">
      <div className="tenor-gif-picker" ref={containerRef}>
        <div className="tenor-gif-picker-header">
          <div className="tenor-tabs">
            <button
              className={`tenor-tab ${mode === 'search' ? 'active' : ''}`}
              onClick={() => setMode('search')}
            >
              GIFs
            </button>
            <button
              className={`tenor-tab ${mode === 'ai' ? 'active' : ''}`}
              onClick={() => setMode('ai')}
            >
              <span className="material-symbols-outlined">animation</span>
              Generate
            </button>
          </div>
          <button className="tenor-close-btn" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {mode === 'ai' ? (
          <AiGifPanel onSelectGif={onSelectGif} onClose={onClose} />
        ) : (
          <>
            <div className="tenor-search-container">
              <span className="material-symbols-outlined search-icon">search</span>
              <input
                type="text"
                placeholder="Search Tenor..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="tenor-search-input"
                autoFocus
              />
              {searchQuery && (
                <button
                  className="clear-search-btn"
                  onClick={() => {
                    setSearchQuery('');
                    loadTrendingGifs();
                  }}
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              )}
            </div>

            <div className="tenor-gif-grid">
              {loading ? (
                <div className="tenor-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading GIFs...</p>
                </div>
              ) : gifs.length === 0 ? (
                <div className="tenor-no-results">
                  <p>No GIFs found. Try a different search!</p>
                </div>
              ) : (
                gifs.map((gif) => (
                  <div
                    key={gif.id}
                    className="tenor-gif-item"
                    onClick={() => handleGifSelect(gif)}
                  >
                    <img
                      src={getGifPreviewUrl(gif)}
                      alt={gif.content_description || 'GIF'}
                      loading="lazy"
                      className="tenor-gif-preview"
                    />
                  </div>
                ))
              )}
            </div>

            <div className="tenor-gif-picker-footer">
              <a
                href="https://tenor.com"
                target="_blank"
                rel="noopener noreferrer"
                className="tenor-attribution"
              >
                Powered by Tenor
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default TenorGifPicker;
